//hooks/useTokenValidator.js
import { useEffect, useRef, useCallback } from 'react';
import { forceLogoutAndRedirect, attemptTokenRefresh } from '../services/apiClient';

const IDLE_CHECK_INTERVAL = 5 * 60 * 1000;

export const useTokenValidator = (isAuthenticated) => {
  const lastCheckRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const isCheckingRef = useRef(false);

  const getCheckUrl = useCallback(() => {
    return window.location.hostname === 'localhost'
      ? 'http://localhost:7080/api/verify'
      : '/api/verify';
  }, []);

  const validateToken = useCallback(async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const response = await fetch(getCheckUrl(), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 401 || response.status === 403) {
        console.warn('🔄 Validator got 401 — attempting refresh...');
        try {
          await attemptTokenRefresh();
        } catch {
          forceLogoutAndRedirect();
        }
        return;
      }

      lastCheckRef.current = Date.now();
    } catch (error) {
      console.warn('⚠️ Token validation network error:', error.message);
    } finally {
      isCheckingRef.current = false;
    }
  }, [getCheckUrl]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() - lastCheckRef.current > 30000) validateToken();
      }
    };
    const handleFocus = () => {
      if (Date.now() - lastCheckRef.current > 30000) validateToken();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') validateToken();
    }, IDLE_CHECK_INTERVAL);
    validateToken();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, validateToken]);
};
